<script setup>
/**
 * MarketingHero — a bold, on-brand hero band. Left-aligned copy (eyebrow +
 * big title + readable subtitle + actions) with an optional framed component
 * to the right. Brand gradient background with diagonal "speed lines" and a
 * soft glow to suggest energy and movement.
 *
 * Slots: #actions (CTA buttons), #note (small print under actions), #media
 * (e.g. an <app-frame>).
 */
defineProps({
  eyebrow: {type: String, default: null},
  // optional personal lead-in rendered inline before the title (e.g. "Hi Grant 👋")
  greeting: {type: String, default: null},
  title: {type: String, required: true},
  subtitle: {type: String, default: null},
});
</script>

<template>
  <section class="mh">
    <div class="mh-streaks" aria-hidden="true"/>
    <div class="mh-glow" aria-hidden="true"/>

    <div class="mh-inner">
      <div class="mh-copy">
        <span v-if="eyebrow" class="mh-eyebrow">{{ eyebrow }}</span>
        <h1 class="mh-title"><span v-if="greeting" class="mh-greet">{{ greeting }} </span>{{ title }}</h1>
        <p v-if="subtitle" class="mh-sub">{{ subtitle }}</p>
        <div v-if="$slots.actions" class="mh-actions">
          <slot name="actions"/>
        </div>
        <div v-if="$slots.note" class="mh-note">
          <slot name="note"/>
        </div>
      </div>

      <div v-if="$slots.media" class="mh-media">
        <slot name="media"/>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.mh {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  padding: 48px 30px;
  color: #fff;
  background: linear-gradient(135deg, #E0314F 0%, #D32946 44%, #A31C35 100%);
  box-shadow: 0 30px 60px -30px rgba(163, 28, 53, 0.75);
}

.mh-streaks {
  position: absolute;
  inset: 0;
  z-index: 0;
  background: repeating-linear-gradient(118deg, transparent 0 38px, rgba(255, 255, 255, 0.05) 38px 40px);
  -webkit-mask-image: linear-gradient(90deg, transparent, #000 35%);
  mask-image: linear-gradient(90deg, transparent, #000 35%);
}

.mh-glow {
  position: absolute;
  z-index: 0;
  top: -45%;
  right: -12%;
  width: 560px;
  height: 560px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.22), transparent 65%);
  pointer-events: none;
}

.mh-inner {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  flex-wrap: wrap;
}

.mh-copy {
  flex: 1 1 360px;
  min-width: 0;
}

.mh-eyebrow {
  display: inline-block;
  font-size: 11.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1.4px;
  padding: 5px 12px;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.28);
}

.mh-title {
  font-size: 36px;
  font-weight: 900;
  line-height: 1.04;
  letter-spacing: -1.2px;
  margin-top: 16px;
  text-wrap: balance;
}

/* personal lead-in, slightly lighter so the question still punches */
.mh-greet {
  font-weight: 700;
  color: rgba(255, 255, 255, 0.96);
}

.mh-sub {
  font-size: 16px;
  line-height: 1.55;
  margin-top: 16px;
  max-width: 520px;
  color: rgba(255, 255, 255, 0.92);
}

.mh-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 28px;
}

.mh-note {
  font-size: 12.5px;
  margin-top: 14px;
  color: rgba(255, 255, 255, 0.82);
}

.mh-media {
  flex: 0 1 440px;
  min-width: 0;
}

@media (min-width: 600px) {
  .mh-title { font-size: 46px; }
}

@media (min-width: 960px) {
  .mh {
    padding: 64px 56px;
  }

  .mh-title { font-size: 54px; }

  .mh-sub { font-size: 18px; }
}

@media (max-width: 700px) {
  .mh-media { flex-basis: 100%; }
}
</style>
